// Phase 13 — SE Claim capture (damage/scheme/display/expiry/price_difference/other)
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Camera, CheckCircle2, MapPin, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { mutate as offlineMutate } from "@/offline/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useDistributors, useRetailers, compressAndUploadPhoto, idempKey, useGps, StatusPill } from "./_helpers";

const TYPES = [
  { code: "damage", label: "Damage" }, { code: "scheme", label: "Scheme" },
  { code: "display", label: "Display" }, { code: "expiry", label: "Expiry" },
  { code: "price_difference", label: "Price Difference" }, { code: "other", label: "Other" },
];

export default function ClaimNew() {
  const nav = useNavigate();
  const dists = useDistributors();
  const rets = useRetailers();
  const gps = useGps();
  const [type, setType] = useState("damage");
  const [amount, setAmount] = useState("");
  const [distributorId, setDistributorId] = useState("");
  const [retailerId, setRetailerId] = useState("");
  const [photos, setPhotos] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  async function addPhoto(e) {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const ref = await compressAndUploadPhoto(f);
      setPhotos(p => [...p, ref]); toast.success("Photo added");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { e.target.value = ""; }
  }
  function removePhoto(i) { setPhotos(p => p.filter((_, idx) => idx !== i)); }

  async function submit() {
    if (!distributorId) return toast.error("Choose a distributor");
    if (!amount || Number(amount) <= 0) return toast.error("Amount must be > 0");
    setBusy(true);
    try {
      const res = await offlineMutate({
        method: "POST", path: "/claims", entity_type: "claims",
        body: {
          claim_type: type, amount: Number(amount),
          distributor_id: distributorId, retailer_id: retailerId || null,
          photos, remarks: remarks || null, gps,
          client_ts: new Date().toISOString(),
          idempotency_key: idempKey("clm"),
          offline_captured: !navigator.onLine,
        },
      });
      if (res.immediate) toast.success("Claim submitted");
      else toast.success("Queued — will sync when online");
      nav("/money/claims");
    } catch (e) {
      toast.error(e?.message || "Submit failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="max-w-lg mx-auto p-4 space-y-4" data-testid="claim-new-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <h1 className="font-semibold flex-1">Raise a claim</h1>
      </div>

      <div>
        <Label>Claim type *</Label>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="clm-type"><SelectValue /></SelectTrigger>
          <SelectContent>{TYPES.map(t => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div>
        <Label>Distributor *</Label>
        <Select value={distributorId || "__none__"} onValueChange={(v) => setDistributorId(v === "__none__" ? "" : v)}>
          <SelectTrigger data-testid="clm-distributor"><SelectValue placeholder="Choose distributor" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— none —</SelectItem>
            {dists.map(d => <SelectItem key={d.id} value={d.id}>{d.code} · {d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Retailer (optional)</Label>
        <Select value={retailerId || "__none__"} onValueChange={(v) => setRetailerId(v === "__none__" ? "" : v)}>
          <SelectTrigger data-testid="clm-retailer"><SelectValue placeholder="Choose retailer" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— none —</SelectItem>
            {rets.map(r => <SelectItem key={r.id} value={r.id}>{r.code} · {r.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Amount (₹) *</Label>
        <Input data-testid="clm-amount" type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </div>

      <div>
        <Label>Photos ({photos.length})</Label>
        <div className="grid grid-cols-3 gap-2 mt-1">
          {photos.map((p, i) => (
            <div key={i} className="relative border rounded bg-emerald-50 border-emerald-500 aspect-square flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <button onClick={() => removePhoto(i)} className="absolute -top-1 -right-1 bg-white border rounded-full p-0.5">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <label htmlFor="clm-photo-input" className="border border-dashed rounded aspect-square flex items-center justify-center cursor-pointer text-neutral-500 hover:border-emerald-400">
            <Camera className="w-4 h-4" />
          </label>
          <input id="clm-photo-input" data-testid="clm-photo" type="file" accept="image/*" capture="environment" className="hidden" onChange={addPhoto} />
        </div>
      </div>

      <div>
        <Label>Remarks</Label>
        <Textarea data-testid="clm-remarks" rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
      </div>

      {gps && (
        <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-800 flex items-center gap-2">
          <MapPin className="w-3 h-3" /> GPS captured
        </div>
      )}

      <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={submit} disabled={busy} data-testid="clm-submit">
        <CheckCircle2 className="w-4 h-4 mr-2" /> {busy ? "Submitting…" : "Submit claim"}
      </Button>
    </div>
  );
}


export function ClaimsList() {
  const nav = useNavigate();
  const [items, setItems] = useState([]); const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/claims?page_size=100").then(r => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, []);
  async function resubmit(c) {
    const remarks = window.prompt("Add updated remarks (optional):", c.remarks || "");
    if (remarks === null) return;
    try {
      await api.post(`/claims/${c.id}/resubmit`, { remarks });
      toast.success("Resubmitted");
      const { data } = await api.get("/claims?page_size=100");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div className="max-w-lg mx-auto p-4 space-y-4" data-testid="claims-list-page">
      <div className="flex items-center justify-between">
        <h1 className="font-semibold text-lg">Claims</h1>
        <Button size="sm" data-testid="claims-new-btn" onClick={() => nav("/money/claims/new")}
                className="bg-emerald-600 hover:bg-emerald-700">+ New claim</Button>
      </div>
      {loading ? <div className="text-neutral-500 text-sm">Loading…</div>
       : items.length === 0 ? <div className="text-neutral-500 text-sm border border-dashed rounded-lg p-6 text-center">No claims yet</div>
       : items.map(c => {
        const isRejected = c.status === "asm_rejected" || c.status === "accounts_rejected";
        return (
         <div key={c.id} className="border rounded-lg p-3 bg-white" data-testid={`claim-row-${c.id}`}>
           <div className="flex justify-between items-start">
             <div className="min-w-0">
               <div className="font-medium truncate">{c.claim_type} · {c.distributor_name_snapshot || "—"}</div>
               <div className="text-xs text-neutral-500 truncate">{c.retailer_name_snapshot || "—"}</div>
               <div className="text-xs text-neutral-400 mt-1">v{c.version}</div>
             </div>
             <div className="text-right">
               <div className="font-semibold">₹ {Number(c.amount).toLocaleString("en-IN")}</div>
               <StatusPill status={c.status} />
             </div>
           </div>
           {(c.asm_reviewer_notes || c.accounts_reviewer_notes) && (
             <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
               📝 {c.accounts_reviewer_notes || c.asm_reviewer_notes}
             </div>
           )}
           {isRejected && (
             <Button size="sm" variant="outline" className="mt-2 w-full" data-testid={`claim-resubmit-${c.id}`} onClick={() => resubmit(c)}>
               Resubmit with corrections
             </Button>
           )}
         </div>
        );
       })
      }
    </div>
  );
}
